import { useState, type SyntheticEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { ApiFailure, api } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { copy } from '../copy/en-CA.ts';
import { useSession } from '../session.tsx';

export function SignInPage() {
  const { setUser } = useSession();
  const location = useLocation();
  // Arriving from "Choose a new password": say it worked, and fill in the email.
  const arrived = location.state as { passwordReset?: unknown; email?: unknown } | null;
  const afterReset = arrived?.passwordReset === true;
  const [email, setEmail] = useState(afterReset && typeof arrived.email === 'string' ? arrived.email : '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const submit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setWorking(true);
    try {
      const { user } = await api.signIn(email, password);
      setUser(user);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="signin-page">
      <div className="wordmark">
        <span className="wordmark-name">{copy.appName}</span>
        <span className="wordmark-sub">{copy.appSub}</span>
      </div>
      <form
        className="card signin-card"
        onSubmit={(e) => {
          void submit(e);
        }}
      >
        <div className="page-title">
          <h1>{copy.signIn.title}</h1>
          <p>{copy.signIn.intro}</p>
        </div>
        {afterReset ? <Notice kind="success">{copy.signIn.passwordReset}</Notice> : null}
        <div className="field">
          <label htmlFor="email">{copy.signIn.email}</label>
          <input id="email" className="input" type="email" autoComplete="username" required value={email} onChange={(e) => { setEmail(e.target.value); }} />
        </div>
        <div className="field">
          <label htmlFor="password">{copy.signIn.password}</label>
          <input
            id="password"
            className="input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => { setPassword(e.target.value); }}
          />
        </div>
        {error ? <Notice kind="error">{error}</Notice> : null}
        <button type="submit" className="btn primary" disabled={working}>
          {working ? copy.signIn.working : copy.signIn.submit}
        </button>
        <p className="small">
          {/* Passes on what was typed, so the email need not be typed twice. */}
          <Link to="/forgot-password" state={{ email }}>
            {copy.signIn.forgot}
          </Link>
        </p>
      </form>
    </div>
  );
}
